import { trigger, state, style, animate, transition, query, group, animateChild, stagger, keyframes } from '@angular/animations';

// Fading Animation
// fadeAnimation: Un'animazione di fade (comparsa/scomparsa) con transizioni di 500ms.
export const fadeAnimation = trigger('fade', [
  state('void', style({ opacity: 0 })),
  transition(':enter, :leave', [ // Entering or leaving
    animate('500ms ease-in-out')
  ])
]);

// Scaling Animation
// scaleAnimation: Un'animazione che scala un elemento da 0 a 1 durante l'entrata e da 1 a 0 durante l'uscita.
export const scaleAnimation = trigger('scale', [
  state('void', style({ transform: 'scale(0)' })),
  transition(':enter', [
    animate('500ms ease-out', style({ transform: 'scale(1)' }))
  ]),
  transition(':leave', [
    animate('500ms ease-in', style({ transform: 'scale(0)' }))
  ])
]);

// Slide-in and Slide-out Animation
// slideInAnimation: Un'animazione di slide (scorrimento) che fa entrare ed uscire un elemento da sinistra.
export const slideInAnimation = trigger('slideIn', [
  transition(':enter', [
    style({ transform: 'translateX(-100%)' }), // Starting position
    animate('{{ timing }} {{ delay }} cubic-bezier(0.58, 0.01, 0, 1.01)', style({ transform: 'translateX(0)' })) // End position
  ], { params: { timing: '300ms', delay: 0 } }),
  transition(':leave', [
    animate('{{ timing }} {{ delay }} ease-out', style({ transform: 'translateX(-100%)' })) // Back to start
  ], { params: { timing: '300ms', delay: 0 } })
]);

// Side-in and Side-out Animation
// sideInAnimation: Un'animazione di side (scorrimento) che fa entrare ed uscire un elemento da destra.
export const slideInRightAnimation = trigger('slideInRight', [
  transition(':enter', [
    style({ transform: 'translateX(100%)' }), // Starting position
    animate('{{ timing }} {{ delay }} cubic-bezier(0.58, 0.01, 0, 1.01)', style({ transform: 'translateX(0)' })) // End position
  ], { params: { timing: '300ms', delay: 0 } }),
  transition(':leave', [
    animate('{{ timing }} {{ delay }} ease-out', style({ transform: 'translateX(100%)' })) // Back to start
  ], { params: { timing: '300ms', delay: 0 } })
]);

// Flip Animation
//flipAnimation: Un'animazione di flip che ruota l'elemento su asse Y di 180 gradi.
export const flipAnimation = trigger('flip', [
  state('flipped', style({
    transform: 'rotateY(180deg)'
  })),
  state('default', style({
    transform: 'rotateY(0deg)'
  })),
  transition('default <=> flipped', [
    animate('600ms ease-in-out')
  ])
]);

// Combined Animation Example: Fade + Slide
// fadeSlideAnimation: Un'animazione combinata di fade e slide.
export const fadeSlideAnimation = trigger('fadeSlide', [
  transition(':enter', [
    style({ opacity: 0, transform: 'translateY(-100%)' }),
    animate('600ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
  ]),
  transition(':leave', [
    animate('600ms ease-in', style({ opacity: 0, transform: 'translateY(-100%)' }))
  ])
]);

// Staggered Animation for child elements
//staggerAnimation: Un'animazione che applica un effetto di "stagger" (ritardo) sugli elementi figli, facendo entrare ogni elemento in sequenza.
export const staggerAnimation = trigger('staggerAnimation', [
  transition('* => *', [
    query(':enter', [
      style({ opacity: 0, transform: 'translateY(20px)' }),
      stagger('100ms', [
        animate('500ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ], { optional: true })
  ])
]);

// Routing Animation for page transitions
//routeAnimation: Un'animazione utilizzabile per le transizioni di routing tra pagine, con un effetto di scorrimento verticale.
export const routeAnimation = trigger('routeAnimation', [
  transition('* <=> *', [
    query(':enter, :leave', style({ position: 'fixed', width: '100%' }), { optional: true }),
    group([
      query(':enter', [
        style({ opacity: 0, transform: 'translateY(100%)' }),
        animate('500ms ease-in-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ], { optional: true }),
      query(':leave', [
        animate('500ms ease-in-out', style({ opacity: 0, transform: 'translateY(-100%)' }))
      ], { optional: true })
    ])
  ])
]);


// Push and fadeOut Animation
// an animation where element is pushed in background while fading out
export const pushFadeOut = trigger('pushFadeOut', [
    state('inactive', style({ opacity: 1, transform: 'scale(1)', filter: 'blur(0)','transform-origin': 'center bottom' })),
    state('active', style({ opacity: 0, transform: 'scale(0.5)', filter: 'blur(5px)', 'transform-origin': 'center bottom' })),
    transition('active=>inactive', [
      animate('700ms ease-in')
    ]),
    transition('inactive=>active', [
      animate('700ms  ease-in')
    ])
])
  
// Push and fadeIn Animation
// an animation where element is pulled from background while fading in
export const pullFadeIn = trigger('pullFadeIn', [
    state('void', style({ opacity: 0, transform: 'scale(1.5)', filter: 'blur(5px)' ,'transform-origin': 'center bottom' })),
    transition('void=>*', [
      animate('500ms ease-in', style({
        opacity: 1,
        transform: 'scale(1)',
        filter: 'blur(0)',
        'transform-origin': 'center bottom'
      }))
    ])
])
  
// Slide-in-up Animation
// element slides up while fading in
export const slideInUp = trigger('slideInUp', [
    state('void', style({
      opacity: 0,
      transform: 'translateY(100px)'
    })),
    transition('void => *', [
      animate('500ms {{ delay }} cubic-bezier(0, 0, 0.02, 0.91)', style({
        opacity: 1,
        transform: 'translateY(0)'
      }))
    ], { params: { delay: 0 } }),
])
  
// Bounce in up Animation
// element enters bouncing twice
export const bounceInUp = trigger('bounceInUp', [
    state('*', style({ transform: 'translateY(300px)', opacity: 0 })),
    state('enter', style({ transform: 'translateY(0)', opacity: 1 })),
    transition('enter<=>*', [
      animate('1200ms  cubic-bezier(0, 0.79, 0.19, 1.09)', keyframes([
        style({ transform: 'translateY(300px)', opacity: 1, offset: 0 }),
        style({ transform: 'translateY(-50px)', opacity: 1, offset: 0.85 }),
        style({ transform: 'translateY(0)', opacity: 1, offset: 1 }),
      ]))
    ])
])

// Slide-in-up Animation staggered: for loops
// element slides up while fading in with a delay of 500ms
  
export const slideInUpStagger =  trigger('slideInUpStagger', [
    transition('* => *', [ 
      query('.stagered:enter', [
          style({ opacity: 0,transform: 'translateY(100px)' }),
          stagger(500, [animate('500s', style({ opacity: 1,transform: 'translateY(0)' }))])
        ], { optional: true }
      )
    ])
])

// Pulse and glow Animation
// element pulses once and emits a colored blurred outline
export const pulse = trigger('pulse', [
    state('inactive', style({ transform: 'scale(1)', outlineStyle:'solid', outlineColor:'transparent',outlineWidth:'0px',outlineOffset:'0px' })),
    state('active', style({ transform: 'scale(1)', outlineStyle:'solid', outlineColor:'transparent',outlineWidth:'0px',outlineOffset:'0px' })),
    transition('inactive => active', [
      animate('1000ms {{ delay }} ease-in-out',keyframes([
        style({ transform: 'scale(1)',outlineWidth:'0px',outlineOffset:'0', outlineColor:'#b9c3ff', offset: 0 }),
        style({ transform: 'scale(0.7)',outlineWidth:'0px',outlineOffset:'0px', outlineColor:'#b9c3ff',offset: 0.4 }),
        style({ transform: 'scale(1.2)', outlineWidth:'7px',outlineOffset:'0px', outlineColor:'#b9c3ff',offset: 0.6 }),
        style({ transform: 'scale(1)',outlineWidth:'12px', outlineOffset:'0px',outlineColor:'transparent', offset: 1 }),
      ]))
    ])
])
  

export const fadInRight = trigger('fadeInRight', [
  state('void', style({
    opacity: 0,
    transform: 'translateX(-100px)'
  })),
  transition('void => *', [
    animate('1s {{ delay }} ease-out', style({
    opacity: 1,
    transform: 'translateX(0%)'
    }))
  ], { params: { delay: '0s' } })
])
  
export const fadeInLeftAnimation =  trigger('fadeInLeft', [
  state('void', style({
    opacity: 0,
    transform: 'translateX(100px)'
  })),
  transition('void => *', [
    animate('1s {{ delay }} ease-out', style({
    opacity: 1,
    transform: 'translateX(0%)'
    }))
  ], { params: { delay: '0s' } })
])
  
export const fadeInUpAnimation = trigger('fadeInUp', [
  state('inactive', style({
    opacity: 0,
    transform: 'translateY(100px)'
  })),
  state('void', style({
    opacity: 0,
    transform: 'translateY(100px)'
  })),
  transition('void => *', [
    animate('1s {{ delay }} ease-out', style({
    opacity: 1,
    transform: 'translateY(0%)'
    }))
  ], { params: { delay: '0s' } }),
  transition('inactive => *', [
    animate('1s {{ delay }} ease-out', style({
    opacity: 1,
    transform: 'translateY(0%)'
    }))
  ], { params: { delay: '0s' } })
    
])